/**
 * OWL2 Annotation Types
 *
 * Defines annotation structures used for metadata in OWL2 ontologies.
 * Annotations can be attached to axioms, entities, and other annotations.
 */
// =============================================================================
// Factory Functions
// =============================================================================
/**
 * Create an annotation property reference.
 */
export function annotationProperty(iri) {
    return { type: 'AnnotationProperty', iri };
}
/**
 * Create an annotation.
 */
export function annotation(property, value, annotations) {
    return {
        property,
        value,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
/**
 * Create an annotation with a literal value.
 */
export function annotationWithLiteral(property, literal, annotations) {
    return annotation(property, literal, annotations);
}
/**
 * Create an annotation with an IRI value.
 */
export function annotationWithIRI(property, iri, annotations) {
    return annotation(property, iri, annotations);
}
// =============================================================================
// Type Guards for Annotation Values
// =============================================================================
/**
 * Check if an annotation value is an IRI.
 */
export function isAnnotationValueIRI(value) {
    return 'kind' in value && (value.kind === 'FullIRI' || value.kind === 'AbbreviatedIRI');
}
/**
 * Check if an annotation value is an anonymous individual.
 */
export function isAnnotationValueAnonymous(value) {
    return 'type' in value && value.type === 'AnonymousIndividual';
}
/**
 * Check if an annotation value is a literal.
 */
export function isAnnotationValueLiteral(value) {
    return 'kind' in value && (value.kind === 'TypedLiteral' || value.kind === 'PlainLiteral');
}
// =============================================================================
// Utility Functions
// =============================================================================
/**
 * Get all annotation property IRIs from a list of annotations (recursive).
 */
export function getAnnotationPropertyIRIs(annotations) {
    const iris = [];
    for (const ann of annotations) {
        iris.push(ann.property.iri);
        if (ann.annotations) {
            iris.push(...getAnnotationPropertyIRIs(ann.annotations));
        }
    }
    return iris;
}
/**
 * Find annotations with a specific property IRI.
 */
export function findAnnotationsByProperty(annotations, propertyIRI) {
    return annotations.filter(ann => {
        if (ann.property.iri.kind !== propertyIRI.kind)
            return false;
        if (ann.property.iri.kind === 'FullIRI' && propertyIRI.kind === 'FullIRI') {
            return ann.property.iri.value === propertyIRI.value;
        }
        if (ann.property.iri.kind === 'AbbreviatedIRI' && propertyIRI.kind === 'AbbreviatedIRI') {
            return ann.property.iri.prefix === propertyIRI.prefix &&
                ann.property.iri.localName === propertyIRI.localName;
        }
        return false;
    });
}
